import type { PickSampleExt } from "@novorender/api";
import type { ReadonlyVec3 } from "gl-matrix";
import { useCallback, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { coordinatesActions, selectCoordinates } from "features/coordinates/coordinatesSlice";
import { useAddCoordinate } from "features/coordinates/hooks/useAddCoordinate";
import { useRemoveCoordinate } from "features/coordinates/hooks/useRemoveCoordinate";
import { measureActions, selectMeasurePickSettings } from "features/measure";
import { selectCurrentPickerGeneratedParametricData } from "features/render";
import { Picker } from "features/render/types";
import { applyCameraDistanceToMeasureTolerance } from "features/render/utils";
import { undoRedoActions } from "features/undoRedo";
import { hasMouseSupport } from "utils/misc";

import {
    mobileMeasureActions,
    mobileMeasureCursorRef,
    PICK_TARGET_OFFSET,
    selectCursorPosition,
} from "./mobileMeasureSlice";
import { nearestWithinRadius } from "./nearestWithinRadius";
import { useMobileMeasureCursor } from "./useMobileMeasureCursor";

const TAP_PROXIMITY_SQ = 30 * 30;

export function useMobileCoordinate() {
    const dispatch = useAppDispatch();
    const {
        state: { view, canvas },
    } = useExplorerGlobals();
    const addCoordinate = useAddCoordinate();
    const removeCoordinate = useRemoveCoordinate();
    const coordinates = useAppSelector(selectCoordinates);
    const coordinatesRef = useRef(coordinates);
    coordinatesRef.current = coordinates;
    const cursorPos = useAppSelector(selectCursorPosition);
    const cursorPosRef = useRef(cursorPos);
    cursorPosRef.current = cursorPos;
    const movingCoordIdRef = useRef<string | null>(null);
    /** Set when a press starts on the remove button; acted on only if it ends as a tap. */
    const pendingRemoveIdRef = useRef<string | null>(null);
    const measurePickSettings = useAppSelector(selectMeasurePickSettings);
    const allowGeneratedParametric = useAppSelector(selectCurrentPickerGeneratedParametricData);

    const onDragUpdate = useCallback(
        async (result: PickSampleExt | null) => {
            if (!view || !result || result.objectId === -1) {
                dispatch(measureActions.selectHoverObj(undefined));
                dispatch(mobileMeasureActions.setCursorVisual("noHit"));
                return;
            }

            dispatch(
                mobileMeasureActions.setCursorVisual(result.sampleType === "surface" ? "surfaceSnap" : "pointSnap"),
            );

            const tolerance = applyCameraDistanceToMeasureTolerance(
                result.position,
                view.renderState.camera,
                measurePickSettings,
            );
            const entity = await view.measure?.core.pickMeasureEntity(
                result.objectId,
                result.position,
                tolerance,
                allowGeneratedParametric.enabled,
            );

            if (entity?.entity && entity.connectionPoint) {
                dispatch(
                    measureActions.selectHoverObj({
                        ...entity.entity,
                        connectionPoint: entity.connectionPoint,
                    }),
                );
            } else {
                dispatch(measureActions.selectHoverObj(undefined));
            }
        },
        [view, dispatch, measurePickSettings, allowGeneratedParametric],
    );

    const onPlace = useCallback(
        async (result: PickSampleExt) => {
            if (!view) {
                return;
            }

            if (result.objectId === -1) {
                return;
            }

            // If we're moving a coordinate, this placement finishes the move
            const movingId = movingCoordIdRef.current ?? mobileMeasureCursorRef.movingCoordinateId;
            if (movingId) {
                mobileMeasureCursorRef.movingCoordinateId = null;
                const oldCoord = coordinatesRef.current.find((c) => c.id === movingId);
                movingCoordIdRef.current = null;

                const coordinateId = crypto.randomUUID();
                const added = await addCoordinate(result.position, {
                    screenOffset: { x: result.x, y: result.y },
                    normal: result.normal,
                    coordinateId,
                    objectId: result.objectId,
                });

                if (added !== false && oldCoord) {
                    dispatch(coordinatesActions.removeCoordinate(movingId));
                    dispatch(
                        undoRedoActions.push({
                            description: "Move coordinate",
                            undoCallback: () => {
                                dispatch(coordinatesActions.removeCoordinate(coordinateId));
                                dispatch(coordinatesActions.addCoordinate(oldCoord));
                            },
                            redoCallback: () => {
                                dispatch(coordinatesActions.removeCoordinate(oldCoord.id));
                                if (added) {
                                    dispatch(coordinatesActions.addCoordinate(added));
                                }
                            },
                        }),
                    );
                }
                dispatch(mobileMeasureActions.setSelectedCoordinateId(null));
                dispatch(mobileMeasureActions.setDpadStyle("normal"));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                return;
            }

            const coordinateId = crypto.randomUUID();
            const added = await addCoordinate(result.position, {
                screenOffset: { x: result.x, y: result.y },
                normal: result.normal,
                coordinateId,
                objectId: result.objectId,
            });

            if (added !== false) {
                dispatch(mobileMeasureActions.setSelectedCoordinateId(null));
                dispatch(mobileMeasureActions.setDpadStyle("normal"));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));

                dispatch(
                    undoRedoActions.push({
                        description: "Add coordinate",
                        undoCallback: () => {
                            dispatch(coordinatesActions.removeCoordinate(coordinateId));
                        },
                        redoCallback: () => {
                            if (added) {
                                dispatch(coordinatesActions.addCoordinate(added));
                            }
                        },
                    }),
                );
            }
        },
        [view, addCoordinate, dispatch],
    );

    /**
     * Finds the placed coordinate nearest to any of the given screen positions, within
     * tapping distance. Pins deliberately take no touch input of their own — a touch that
     * lands on one never reaches the canvas the camera controller listens on — so touch
     * interaction with a pin goes through this instead of DOM hit-testing.
     */
    const findCoordinateNear = useCallback(
        (...positions: { x: number; y: number }[]) => {
            if (!view) {
                return undefined;
            }
            const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
            const onScreen = coordinatesRef.current.flatMap((coord) => {
                const screen = toScreenSpace(coord.point.position as ReadonlyVec3);
                return screen ? [{ id: coord.id, screen }] : [];
            });
            return nearestWithinRadius(onScreen, positions, TAP_PROXIMITY_SQ);
        },
        [view],
    );

    /** Moves the D-pad onto a pin and arms it to be re-placed by the next drag. */
    const armMove = useCallback(
        (hit: { id: string; screen: { [index: number]: number } }) => {
            movingCoordIdRef.current = hit.id;
            mobileMeasureCursorRef.movingCoordinateId = hit.id;
            dispatch(mobileMeasureActions.setSelectedCoordinateId(hit.id));
            dispatch(
                mobileMeasureActions.setCursorPosition({
                    x: hit.screen[0] - PICK_TARGET_OFFSET.x,
                    y: hit.screen[1] - PICK_TARGET_OFFSET.y,
                }),
            );
            dispatch(mobileMeasureActions.setIndicatorPosition({ x: hit.screen[0], y: hit.screen[1] }));
            dispatch(mobileMeasureActions.setCursorActive(true));
            dispatch(mobileMeasureActions.setDpadStyle("normal"));
            dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
        },
        [dispatch],
    );

    /**
     * The pin whose remove button is under the given point. Every pin shows one on touch,
     * and none of them take pointer input of their own, so the rects are read from the DOM
     * — that way the hit area tracks each label's real width and position rather than a
     * guess at its layout.
     */
    const hitRemoveButton = useCallback(
        (canvasPos: { x: number; y: number }) => {
            if (!canvas) {
                return undefined;
            }
            const canvasRect = canvas.getBoundingClientRect();
            const x = canvasPos.x + canvasRect.left;
            const y = canvasPos.y + canvasRect.top;
            return coordinatesRef.current.find((coord) => {
                const rect = document.getElementById(`coord-${coord.id}-label-remove`)?.getBoundingClientRect();
                return rect && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
            })?.id;
        },
        [canvas],
    );

    const onTapElsewhere = useCallback(
        (canvasPos: { x: number; y: number }) => {
            // Hit-tested here, on touchstart, so the rect is read while the label is still
            // laid out where the finger landed. Nothing is removed until the gesture ends
            // as a stationary tap.
            const removeTargetId = hitRemoveButton(canvasPos);
            pendingRemoveIdRef.current = removeTargetId ?? null;
            if (removeTargetId) {
                return;
            }
            movingCoordIdRef.current = null;
            mobileMeasureCursorRef.movingCoordinateId = null;
            dispatch(mobileMeasureActions.setSelectedCoordinateId(findCoordinateNear(canvasPos)?.id ?? null));
            dispatch(mobileMeasureActions.setDpadStyle("normal"));
            dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
            dispatch(mobileMeasureActions.setIndicatorTransform(null));
        },
        [dispatch, findCoordinateNear, hitRemoveButton],
    );

    const onCanvasTap = useCallback(() => {
        const removeTargetId = pendingRemoveIdRef.current;
        pendingRemoveIdRef.current = null;
        const coord = coordinatesRef.current.find((c) => c.id === removeTargetId);
        if (!coord) {
            return;
        }
        dispatch(mobileMeasureActions.setSelectedCoordinateId(null));
        removeCoordinate(coord);
    }, [dispatch, removeCoordinate]);

    /** Long-pressing a pin picks it up, the gesture the marker used to handle itself. */
    const onCanvasLongPress = useCallback(
        (canvasPos: { x: number; y: number }) => {
            pendingRemoveIdRef.current = null;
            const hit = findCoordinateNear(canvasPos);
            if (hit) {
                armMove(hit);
            }
        },
        [findCoordinateNear, armMove],
    );

    /** A pinch cancels an armed move; otherwise the next release would apply it. */
    const onAbort = useCallback(() => {
        pendingRemoveIdRef.current = null;
        movingCoordIdRef.current = null;
        mobileMeasureCursorRef.movingCoordinateId = null;
        dispatch(mobileMeasureActions.setDpadStyle("normal"));
        dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
    }, [dispatch]);

    const onDpadLongPress = useCallback(() => {
        const pos = cursorPosRef.current;
        if (!pos) {
            return;
        }
        const pickPos = {
            x: pos.x + PICK_TARGET_OFFSET.x,
            y: pos.y + PICK_TARGET_OFFSET.y,
        };
        const hit = findCoordinateNear(pos, pickPos);
        if (hit) {
            armMove(hit);
        }
    }, [findCoordinateNear, armMove]);

    useMobileMeasureCursor({
        picker: Picker.Coordinates,
        onPlace,
        onDragUpdate,
        onTapElsewhere,
        onDpadLongPress,
        onCanvasTap,
        onCanvasLongPress,
        onAbort,
        enabled: !hasMouseSupport(),
        sampleDiscRadius: 4,
        persistent: true,
    });
}
